// theme-by-host.service.ts
import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

type BrandKey = 'harpia' | 'default';

interface HostThemeColors {
  primaryBg: string;
  primaryText: string;
  primaryBgHover: string;
  primaryBgBorder: string;

  primaryBtn: string;
  primaryBtnText : string,
  secondaryBg: string;
  tertiaryBg: string;

  weeklyChartPrimary: string;
  weeklyChartSecondary: string;

  monthlyChartPrimary: string;
  monthlyChartSecondary: string;
  monthlyChartTertiary: string;
}

interface HostThemeConfig {
  colors: HostThemeColors;
  logo: string;
  sidebarLogo: string;
  solutionsLogo: string;
  sidebarSolutionsLogo: string;
  solutionsIcon: string;
  favIcon: string;
  mobileIcon: string;
}

@Injectable({ providedIn: 'root' })
export class HostThemeService {
  private readonly themes: Record<BrandKey, HostThemeConfig> = {
    harpia: {
      colors: {
        primaryBg: '#013049',
        primaryText: '#fff',
        primaryBgHover: '#013049',
        primaryBgBorder: '#013049',

        primaryBtn: '#ec263a',
        primaryBtnText : '#fff',
        secondaryBg: '#ec263a',
        tertiaryBg: '#f2f2f2',

        weeklyChartPrimary: '#1F7A5C',
        weeklyChartSecondary: '#cc0000',

        monthlyChartPrimary: '#a0a0a0',
        monthlyChartSecondary: '#1f3f57',
        monthlyChartTertiary: '#1f7a5c',
      },
      logo: 'assets/images/brand/suamarca.png',
      sidebarLogo: 'assets/images/brand/suamarca2.png',
      solutionsLogo: 'assets/images/brand/harpia.png',
      sidebarSolutionsLogo: 'assets/images/brand/harpia3.png',
      solutionsIcon: 'assets/images/brand/harpia-icon.png',
      favIcon: 'assets/images/brand/harpia-icon.png',
      mobileIcon: 'assets/images/brand/harpia-icon.png'
    },

    default: {
      colors: {
        primaryBg: '#0052cc',
        primaryText: '#45852b',
        primaryBgHover: '#0e62df',
        primaryBgBorder: '#0e62df',

        primaryBtn: '#0052cc',
        primaryBtnText : '#fff',
        secondaryBg: '#0052cc',
        tertiaryBg: '#0052cc',

        weeklyChartPrimary: '#0CC7E0',
        weeklyChartSecondary: '#094FDB',

        monthlyChartPrimary: '#a0a0a0',
        monthlyChartSecondary: '#1f3f57',
        monthlyChartTertiary: '#1F7A5C',
      },
      logo: 'assets/images/brand/lotep.png',
      sidebarLogo: 'assets/images/brand/lotep2.png',
      solutionsLogo: 'assets/images/brand/harpia.png',
      sidebarSolutionsLogo: 'assets/images/brand/harpia2.png',
      solutionsIcon: 'assets/images/brand/harpia-icon.png',
      favIcon: 'assets/images/brand/lotep-icon.png',
      mobileIcon: 'assets/images/brand/lotep-icon.png'
    },
  };

  private activeConfig: HostThemeConfig = this.themes.default;

  constructor(@Inject(DOCUMENT) private document: Document) { }

  initTheme(): void {
    const host = (this.document.defaultView?.location.hostname || '').toLowerCase();
    const brand = this.resolveBrand(host);

    this.activeConfig = this.themes[brand];
    this.applyColors(this.activeConfig.colors);
    this.applyFavicon(this.activeConfig.favIcon);
    this.applyMobileIcon(this.activeConfig.mobileIcon);
  }

  private resolveBrand(host: string): BrandKey {
    if (host.startsWith('localhost')) {
      // teste de host em dev
      return 'harpia';
    }

    if (host.includes('harpiatechnology')) {
      return 'harpia';
    }


    return 'default';
  }

  private applyColors(colors: HostThemeColors): void {
    const root = this.document.documentElement;

    root.style.setProperty('--primary-bg-color', colors.primaryBg);
    root.style.setProperty('--primary-text-color', colors.primaryText);
    root.style.setProperty('--primary-bg-hover', colors.primaryBgHover);
    root.style.setProperty('--primary-bg-border', colors.primaryBgBorder);

    root.style.setProperty('--primary-btn-color', colors.primaryBtn);
    root.style.setProperty('--primary-btn-text', colors.primaryBtnText);
    root.style.setProperty('--secondary-bg-color', colors.secondaryBg);
    root.style.setProperty('--tertiary-bg-color', colors.tertiaryBg);

    root.style.setProperty('--weekly-chart-primary', colors.weeklyChartPrimary);
    root.style.setProperty('--weekly-chart-secondary', colors.weeklyChartSecondary);

    root.style.setProperty('--monthly-chart-primary', colors.monthlyChartPrimary);
    root.style.setProperty('--monthly-chart-secondary', colors.monthlyChartSecondary);
    root.style.setProperty('--monthly-chart-tertiary', colors.monthlyChartTertiary);
  }

  get logo(): string {
    return this.activeConfig.logo;
  }

  get mobileLogo(): string {
    return this.activeConfig.mobileIcon
  }

  get sidebarLogo(): string {
    return this.activeConfig.sidebarLogo;
  }

  get solutionsLogo(): string {
    return this.activeConfig.solutionsLogo;
  }

  get sidebarSolutionsLogo(): string {
    return this.activeConfig.sidebarSolutionsLogo;
  }

  get solutionsIcon(): string {
    return this.activeConfig.solutionsIcon;
  }

  private applyFavicon(faviconPath: string): void {
    const head = this.document.head;

    const baseHref = this.document.querySelector('base')?.href ?? this.document.baseURI;
    const absoluteHref = new URL(faviconPath, baseHref).toString();

    const rels = ['icon', 'shortcut icon'];

    for (const rel of rels) {
      let link = head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;

      if (!link) {
        link = this.document.createElement('link');
        link.rel = rel;
        head.appendChild(link);
      }

      link.type = faviconPath.endsWith('.png') ? 'image/png' : 'image/x-icon';
      link.href = absoluteHref;
    }
  }

  private applyMobileIcon(path: string): void {
    const head = this.document.head;
    const baseHref = this.document.querySelector('base')?.href ?? this.document.baseURI;
    const absoluteHref = new URL(path, baseHref).toString();

    let link = head.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement | null;
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'apple-touch-icon';
      head.appendChild(link);
    }

    link.href = absoluteHref;
  }
}
